import { Link, useParams, useSearch } from '@tanstack/react-router'
import { useTranslation } from '@kostbar/i18n'
import { ProductDetailTabs } from '../components/product-detail-tabs'
import { useProductDetail } from '../hooks/use-product-detail'
import { ProductKindBadge } from '../components/product-kind-badge'
import { VariantSourceBanner } from '../components/variant-source-banner'
import { Card, CardContent } from '@/components/ui/card'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Skeleton } from '@/components/ui/skeleton'
import { PermissionGate } from '@/shared/components/permission-gate'
import { ACTIONS } from '@/shared/constants/actions'

export function ProductDetailPage() {
  const { sku = '' } = useParams({ strict: false })
  // `?tab=stockUsage` (the POS layout editor's stock breakdown) opens "Lagerverbrauch" directly.
  const { tab } = useSearch({ strict: false })
  const { t: tProducts } = useTranslation('products')
  const { data: product, isLoading } = useProductDetail(sku)

  return (
    <PermissionGate action={ACTIONS.PRODUCT_VIEW}>
      <div>
        <div className="mb-6">
          <Breadcrumb className="mb-4">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/products">{tProducts('detail.breadcrumb')}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{sku}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {isLoading ? (
            <Skeleton className="h-8 w-64" />
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-semibold">{product?.name ?? sku}</h1>
              {product && <ProductKindBadge product={product} />}
            </div>
          )}
          <p className="text-sm text-muted-foreground mt-1">{sku}</p>
        </div>

        {product?.variantSourceSku && (
          <VariantSourceBanner
            sourceSku={product.variantSourceSku}
            share={product.variantShare ?? 1}
            pausedBySource={product.pausedBySource === true}
          />
        )}

        <Card>
          <CardContent className="pt-6">
            <ProductDetailTabs
              key={sku}
              sku={sku}
              initialTab={tab === 'stockUsage' ? 'stockUsage' : undefined}
            />
          </CardContent>
        </Card>
      </div>
    </PermissionGate>
  )
}
